import React, { useContext, useState } from 'react';
import CountBadge from '../../components/notifications/CountBadge';
import HeaderToolbar, {
    HeaderDrawerExtras,
    HeaderDrawerFooter,
} from '../../components/nav/HeaderToolbar';
import { useNotificationsOptional } from '../../context/NotificationContext';
import { WalletAddressContext } from '../../constant/constant';
import { cronpayLogoSrc } from '../../config/brandAssets';
import { adminNavigation, navigation } from '../../config/data';
import { useTheme } from '../../context/ThemeContext';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const { isAdmin } = useContext(WalletAddressContext);
    const { resolvedTheme } = useTheme();
    const logoSrc = cronpayLogoSrc(resolvedTheme);
    const notifications = useNotificationsOptional();
    const menuBadges = notifications?.menuBadges ?? {
        ongoing: 0,
        disputes: 0,
        tasks: 0,
        companyJob: 0,
    };

    const getNavBadge = (item) => {
        if (item.badgeKey === 'ongoing') return menuBadges.ongoing;
        if (item.badgeKey === 'disputes') return menuBadges.disputes;
        if (item.id === 'company-job') return menuBadges.companyJob;
        return 0;
    };

    const closeDialog = () => setIsOpen(false);

    return (
        <>
            <div className="h-14 w-full sm:h-16 lg:hidden" aria-hidden />
            <div className="hidden lg:block h-16 w-[calc(100%-265px)] ml-[265px]"></div> {/* Desktop spacer matching App.js margin */}
            <header className="fixed left-0 top-0 z-40 w-full border-b border-zinc-200 bg-white/95 shadow-sm backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-950/95">
                <div className="flex h-14 items-center justify-between gap-2 px-2 sm:h-16 sm:gap-3 sm:px-4 lg:px-6">
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
                        <button
                            type="button"
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 lg:hidden dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
                            onClick={() => setIsOpen(true)}
                        >
                            <span className="sr-only">Open main menu</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 6h18M3 18h18"></path>
                            </svg>
                        </button>

                        <Link to="/" className="inline-flex shrink-0 items-center">
                            <img
                                src={logoSrc}
                                alt="CronPay"
                                className="block h-8 w-auto object-contain object-left transition-opacity hover:opacity-80 sm:h-9 lg:h-10"
                            />
                        </Link>
                    </div>

                    <HeaderToolbar />
                </div>
            </header>

            {/* MOBILE DRAWER MENU */}
            <div className={`fixed inset-0 z-[9999] lg:hidden transition-all duration-300 ${isOpen ? 'visible' : 'invisible'}`} role="dialog" aria-modal="true">
                {/* Dark Overlay */}
                <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={closeDialog}></div>

                {/* Menu Content */}
                <div className={`fixed inset-y-0 left-0 z-[10000] w-full max-w-[300px] border-r border-zinc-200 bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:border-zinc-800 dark:bg-zinc-950 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="flex h-full flex-col">
                        {/* Header within drawer */}
                        <div className="flex items-center justify-between border-b border-zinc-200 p-5 dark:border-zinc-800">
                            <img
                                src={logoSrc}
                                alt="CronPay"
                                className="block h-9 w-auto object-contain"
                            />
                            <button type="button" className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white" onClick={closeDialog}>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Navigation Links */}
                        <div className="flex-1 overflow-y-auto px-4 py-6">
                            <HeaderDrawerExtras />
                            <nav className="flex flex-col gap-2">
                                {(isAdmin ? adminNavigation : navigation).map((item) => {
                                    const isActive = location.pathname === item.href;
                                    return (
                                        <Link
                                            key={item.id}
                                            to={item.href}
                                            onClick={closeDialog}
                                            className={`flex items-center gap-4 rounded-xl px-4 py-3.5 text-[15px] font-semibold transition-colors
                                            ${isActive
                                                    ? 'bg-teal-700 text-white shadow-sm dark:bg-teal-600'
                                                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100'}`}
                                        >
                                            <i className={`${item.icon} text-lg`}></i>
                                            <span className="flex-1">{item.name}</span>
                                            <CountBadge
                                                value={getNavBadge(item)}
                                                className={
                                                    isActive
                                                        ? '!border-white/30 !bg-white/20 !text-white'
                                                        : ''
                                                }
                                            />
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* Profile + appearance */}
                        <div className="border-t border-zinc-200 bg-zinc-50/80 p-5 dark:border-zinc-800 dark:bg-zinc-900/40">
                            <HeaderDrawerFooter />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Header;
